import type { ReactNode } from "react";
import { Suspense, Component, useMemo, useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { useGLTF, OrbitControls } from "@react-three/drei";
import { clone as skeletonClone } from "three/examples/jsm/utils/SkeletonUtils";
import * as THREE from "three";
import Spinner from "../Loader/Spinner";

class GltfErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function AvatarModel({
  url,
  headOnly,
  onLoad,
}: {
  url: string;
  headOnly?: boolean;
  onLoad?: () => void;
}) {
  const { scene } = useGLTF(url);
  const cloned = useMemo(() => {
    const c = skeletonClone(scene);
    const box = new THREE.Box3().setFromObject(c);
    if (headOnly) {
      const center = box.getCenter(new THREE.Vector3());
      const faceY = box.min.y + (box.max.y - box.min.y) * 0.82;
      c.position.set(-center.x, -faceY, -center.z);
    } else {
      const center = box.getCenter(new THREE.Vector3());
      c.position.sub(center);
    }
    return c;
  }, [scene]);

  useEffect(() => {
    onLoad?.();
  }, []);

  // eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (Object3D), not a DOM attribute
  return <primitive object={cloned} />;
}

type AvatarPreview3dProps = {
  url: string;
  interactive?: boolean;
  className?: string;
  canvasClassName?: string;
};

export default function AvatarPreview3d({
  url,
  interactive = false,
  className,
  canvasClassName,
}: AvatarPreview3dProps) {
  const [modelLoaded, setModelLoaded] = useState(false);

  useEffect(() => {
    setModelLoaded(false);
  }, [url]);

  return (
    <GltfErrorBoundary fallback={<p className="text-sm text-gray-400 ml-4">-</p>}>
      <div className={className} style={{ position: "relative" }}>
        {!modelLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner style={{ position: "static", margin: 0 }} />
          </div>
        )}
        <div
          className={canvasClassName || "w-full h-full"}
          style={modelLoaded ? undefined : { opacity: 0 }}
        >
          <Canvas camera={{ position: interactive ? [0, -0.25, 1.0] : [0, 0, 0.8], fov: 28 }}>
            {/* eslint-disable react/no-unknown-property -- react-three-fiber intrinsic props
                (intensity/position), not DOM attributes; the rule only knows DOM. */}
            <ambientLight intensity={0.6} />
            <directionalLight position={[3, 3, 3]} intensity={1} />
            <directionalLight position={[-3, 1, -3]} intensity={0.4} />
            {/* eslint-enable react/no-unknown-property */}
            <Suspense fallback={null}>
              <AvatarModel url={url} headOnly={!interactive} onLoad={() => setModelLoaded(true)} />
            </Suspense>
            {interactive && <OrbitControls enablePan={false} minDistance={0.8} maxDistance={4} />}
          </Canvas>
        </div>
      </div>
    </GltfErrorBoundary>
  );
}
